import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { deliveryCost, deliveryModesOf, DELIVERY_LABEL, isInPerson, meetingDates, PAYMENT_LABEL, SAFE_PAYMENTS, slotFor, WEEKDAY_LABEL, type DeliveryMode, type MarketPaymentMethod } from "../../shared/market";
import { ZONE_LABEL, type RatedZone } from "../../shared/shipping";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError, api, imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { useMarketCart } from "./cart";
import { SellerChip, useArs } from "./components";
import { useCouponOff } from "./CouponsTab";
import type { CouponCheck } from "../../shared/marketCoupons";

/** /mercado/comprar/:handle — the order with one seller: cards, delivery, payment, contact. */
export function MarketCheckoutPage() {
  const { handle = "" } = useParams();
  const { t, locale } = useI18n();
  const ars = useArs();
  const auth = useAuth();
  const navigate = useNavigate();
  const { carts, setQuantity, setPrices, remove, clear } = useMarketCart();
  const seller = useAsync((signal) => marketApi.seller(handle, signal), [handle]);
  const profile = useAsync((signal) => (auth.user ? api.profile(signal) : Promise.resolve(null)), [auth.user?.id]);
  const lines = carts[handle] ?? [];
  const [mode, setMode] = useState<DeliveryMode | null>(null);
  const [zone, setZone] = useState<RatedZone | "">("");
  const [payment, setPayment] = useState<MarketPaymentMethod | null>(null);
  const [meetingDate, setMeetingDate] = useState("");
  const [buyer, setBuyer] = useState({ name: "", phone: "", address: "", city: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState<CouponCheck | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const couponOff = useCouponOff();
  // Coupons this buyer has from this shop (personal or for a group), offered at the checkout.
  const mine = useAsync((signal) => (auth.user ? marketApi.myCoupons(handle, signal) : Promise.resolve([])), [handle, auth.user?.id]);
  const offerId = lines.find((l) => l.offerId)?.offerId ?? null;
  const linesKey = lines.map((l) => `${l.listingId}x${l.quantity}`).join(",");

  /** The message for a refused coupon (MarketError code, with the minimum when there is one). */
  const couponMessage = (err: unknown) => {
    const code = err instanceof ApiError ? err.code : "";
    if (!code.startsWith("coupon_")) return t("mkt.couponErr.other");
    const min = (err as ApiError).details as { min?: number } | undefined;
    return t(`mkt.couponErr.${code}` as never, { min: ars(min?.min ?? 0) });
  };
  const applyCoupon = async (code: string) => {
    if (!code.trim()) return;
    setChecking(true);
    setCouponError(null);
    try {
      setCoupon(await marketApi.checkCoupon({ seller: handle, code: code.trim(), lines: lines.map((l) => ({ listingId: l.listingId, quantity: l.quantity })), offerId }));
    } catch (err) {
      setCoupon(null);
      setCouponError(couponMessage(err));
    } finally {
      setChecking(false);
    }
  };
  // The cards changed: check the coupon again (its discount, or whether it still applies).
  useEffect(() => {
    if (coupon) void applyCoupon(coupon.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesKey]);

  // Mercado Pago first: preselected whenever the seller takes it (the purchase with buyer protection).
  useEffect(() => {
    if (seller.data?.payments.includes("mercadopago")) setPayment((p) => p ?? "mercadopago");
  }, [seller.data]);

  useEffect(() => {
    const p = profile.data;
    if (p) setBuyer((b) => ({ ...b, name: b.name || [p.firstName, p.lastName].filter(Boolean).join(" ") || p.name, phone: b.phone || p.phone, address: b.address || [p.addressLine, p.addressLine2].filter(Boolean).join(", "), city: b.city || p.city }));
  }, [profile.data]);

  if (!auth.loading && !auth.user) return <Navigate to={`/ingresar?next=${encodeURIComponent(`/mercado/comprar/${handle}`)}`} replace />;
  if (seller.error) return <ErrorMessage message={t("mkt.sellerNotFound")} />;
  if (seller.loading || !seller.data) return <Loading />;
  const s = seller.data;
  if (!lines.length) {
    return (
      <div className="container section status">
        <p>{t("mkt.cartEmpty")}</p>
        <Link to={`/vendedores/${handle}`} className="btn btn-primary">
          {t("mkt.backToShop")}
        </Link>
      </div>
    );
  }

  const modes = deliveryModesOf(s.delivery);
  // The shop can't sell yet (no way to deliver or to get paid): say so instead of a dead button.
  const usablePayments = s.payments.filter((p) => p !== "cash" || modes.some(isInPerson));
  const missing = [modes.length === 0 && t("mkt.cantSellDelivery"), usablePayments.length === 0 && t("mkt.cantSellPayment")].filter(Boolean);
  if (missing.length) {
    return (
      <div className="container section status">
        <p>{t("mkt.cantSellYet", { what: missing.join(t("mkt.and")) })}</p>
        <Link to={`/vendedores/${handle}`} className="btn btn-primary">
          {t("mkt.backToShop")}
        </Link>
      </div>
    );
  }
  const subtotal = lines.reduce((n, l) => n + l.priceArs * l.quantity, 0);
  const shipping = mode ? deliveryCost(s.delivery, mode) : null;
  // Never more than what the cards add up to (the server caps it the same way).
  const couponArs = coupon ? Math.min(coupon.discountArs, subtotal) : 0;
  const total = subtotal - couponArs + (shipping ?? 0);
  const offerLine = lines.find((l) => l.offerId);
  // The shop's minimum purchase: the cards before a coupon; an accepted offer is exempt (the server checks it the same way).
  const missingForMin = s.minOrderArs && !offerLine ? Math.max(0, s.minOrderArs - subtotal) : 0;
  // The cards from other sellers in the cart: separate orders, paid separately.
  const others = Object.entries(carts).filter(([h, ls]) => h !== handle && ls.length > 0);
  // In person: the buyer picks one of the seller's days; the time window is the seller's.
  const schedule = mode && isInPerson(mode) ? s.delivery.schedule : null;
  const dates = meetingDates(schedule);
  const cashBlocked = payment === "cash" && mode !== null && !isInPerson(mode);
  // New seller and a big order: only Mercado Pago or cash in hand (the server checks it too).
  const safeOnly = s.safePayAboveArs != null && total > s.safePayAboveArs;
  const unsafe = (p: MarketPaymentMethod) => safeOnly && !SAFE_PAYMENTS.includes(p);
  const ready =
    mode && payment && !missingForMin && !cashBlocked && !unsafe(payment) && buyer.name.trim().length > 1 && (mode !== "hand" || zone) && (mode !== "ship" || (buyer.address && buyer.city)) && (!schedule || meetingDate) && accepted;
  /** "Sábado 10/10, de 9:00 a 17:00": each day with that weekday's window. */
  const dayLabel = (iso: string) => {
    const d = new Date(`${iso}T12:00:00Z`);
    const slot = slotFor(schedule, iso);
    return `${WEEKDAY_LABEL[d.getUTCDay()][locale]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}${slot ? ` · ${slot.from}–${slot.to}` : ""}`;
  };
  const chosenSlot = slotFor(schedule, meetingDate);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setSending(true);
    setError(null);
    try {
      const res = await marketApi.createOrder({
        // The prices shown: if one changed since, the order stops and the cart shows the new ones.
        lines: lines.map((l) => (l.offerId ? { listingId: l.listingId, quantity: l.quantity } : { listingId: l.listingId, quantity: l.quantity, priceArs: l.priceArs })),
        offerId: offerLine?.offerId ?? null,
        paymentMethod: payment!,
        deliveryMode: mode!,
        zone: mode === "hand" ? (zone as RatedZone) : null,
        meetingDate: schedule ? meetingDate : null,
        buyer,
        couponCode: coupon?.code ?? null,
      });
      clear(handle);
      if (res.redirectUrl) window.location.assign(res.redirectUrl);
      else navigate(`/mercado/pedido/${res.number}`);
    } catch (err) {
      setSending(false);
      const code = err instanceof ApiError ? err.code : "";
      // A refused coupon: drop it and say why; the order can go ahead without it.
      if (code === "price_changed") {
        const prices = ((err as ApiError).details as { prices?: { listingId: number; priceArs: number }[] } | undefined)?.prices ?? [];
        setPrices(handle, prices);
        // A coupon's discount was worked out on the old prices: it's applied again by hand.
        if (coupon) setCoupon(null);
        setError(`${t("mkt.errPriceChanged", { names: prices.map((p) => lines.find((l) => l.listingId === p.listingId)?.name ?? "").filter(Boolean).join(", ") })}${coupon ? ` ${t("mkt.errPriceChangedCoupon")}` : ""}`);
        return;
      }
      // The shop's minimum as the server has it (it may have changed since the page loaded).
      if (code === "below_min_order") {
        const min = ((err as ApiError).details as { min?: number } | undefined)?.min ?? 0;
        setError(t("mkt.belowMinOrder", { min: ars(min), missing: ars(Math.max(0, min - subtotal)) }));
        return;
      }
      if (code.startsWith("coupon_")) {
        setCoupon(null);
        setCouponError(couponMessage(err));
        setError(couponMessage(err));
        return;
      }
      setError(
        code === "not_enough" || code === "listing_unavailable"
          ? t("mkt.errStock")
          : code === "meeting_date"
            ? t("mkt.errMeeting")
          : code === "seller_on_vacation"
            ? t("mkt.errVacation")
          : code === "new_seller_safe_payment"
            ? t("mkt.newSellerSafePay", { max: ars(s.safePayAboveArs ?? 50000) })
            : code === "offer_unavailable"
              ? t("mkt.errOffer")
              : t("error.generic"),
      );
    }
  };

  return (
    <div className="container section market-checkout">
      <h1>{t("mkt.checkoutTitle", { handle })}</h1>
      <SellerChip seller={s} />
      {others.length > 0 && (
        <div className="notice notice-warn">
          <p>{t("mkt.otherCarts", { handle })}</p>
          <ul>
            {others.map(([h, ls]) => (
              <li key={h}>
                <Link to={`/mercado/comprar/${h}`}>
                  {t("mkt.otherCart", { handle: h, count: ls.reduce((n, l) => n + l.quantity, 0), total: ars(ls.reduce((n, l) => n + l.priceArs * l.quantity, 0)) })}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <form className="checkout-layout" onSubmit={submit}>
        <div className="stack">
          <section aria-labelledby="mc-cards">
            <h2 id="mc-cards" className="small-title">
              {t("mkt.cards")}
            </h2>
            <ul className="mc-lines">
              {lines.map((l) => (
                <li key={l.listingId} className="mc-line">
                  {l.photo && <img src={imageUrl(l.photo)} alt="" width={56} height={56} />}
                  <div>
                    <Link to={`/mercado/${l.listingId}`}>{l.name}</Link>
                    {l.offerId && <span className="pill">{t("mkt.offerPriceTag")}</span>}
                    <div className="muted small">{ars(l.priceArs)}</div>
                  </div>
                  {l.max > 1 && !l.offerId ? (
                    <input
                      type="number"
                      aria-label={t("mkt.quantityOf", { name: l.name })}
                      min={1}
                      max={l.max}
                      value={l.quantity}
                      onChange={(e) => setQuantity(handle, l.listingId, Number(e.target.value) || 1)}
                      className="qty-input"
                    />
                  ) : (
                    <span>× {l.quantity}</span>
                  )}
                  <button type="button" className="btn btn-link btn-sm" onClick={() => remove(handle, l.listingId)}>
                    {t("mkt.remove")}
                  </button>
                </li>
              ))}
            </ul>
            {missingForMin > 0 && <p className="notice notice-warn">{t("mkt.belowMinOrder", { min: ars(s.minOrderArs!), missing: ars(missingForMin) })}</p>}
            <Link to={`/vendedores/${handle}`} className="btn btn-link btn-sm">
              {t("mkt.addMoreFromSeller")}
            </Link>
          </section>

          <fieldset className="radio-cards">
            <legend>{t("mkt.delivery")}</legend>
            {modes.map((m) => (
              <label key={m} className={mode === m ? "radio-card is-selected" : "radio-card"}>
                <input type="radio" name="delivery" value={m} checked={mode === m} onChange={() => setMode(m)} />
                <span>
                  <strong>{DELIVERY_LABEL[m][locale]}</strong>
                  <span className="muted small">
                    {m === "hand"
                      ? `${s.delivery.hand!.zones.map((z) => ZONE_LABEL[z][locale]).join(", ")} · ${s.delivery.hand!.costArs ? ars(s.delivery.hand!.costArs) : t("mkt.free")}`
                      : m === "ship"
                        ? `${s.delivery.ship!.carriers || ""} · ${s.delivery.ship!.costArs === null ? t("mkt.quoted") : ars(s.delivery.ship!.costArs)}`
                        : m === "pickup"
                          ? t("mkt.pickupAfterPay")
                          : t("mkt.arrangeChat")}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          {mode === "hand" && (
            <div className="field">
              <label htmlFor="mc-zone">{t("mkt.yourZone")}</label>
              <select id="mc-zone" value={zone} onChange={(e) => setZone(e.target.value as RatedZone)} required>
                <option value="">{t("mkt.choose")}</option>
                {s.delivery.hand!.zones.map((z) => (
                  <option key={z} value={z}>
                    {ZONE_LABEL[z][locale]}
                  </option>
                ))}
              </select>
              {s.delivery.hand!.areas && <span className="field-hint">{s.delivery.hand!.areas}</span>}
            </div>
          )}

          {schedule && (
            <div className="field">
              <label htmlFor="mc-day">{t("mkt.meetingDay")}</label>
              <select id="mc-day" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} required>
                <option value="">{t("mkt.choose")}</option>
                {dates.map((d) => (
                  <option key={d} value={d}>
                    {dayLabel(d)}
                  </option>
                ))}
              </select>
              {chosenSlot && <span className="field-hint">{t("mkt.meetingWindow", { from: chosenSlot.from, to: chosenSlot.to })}</span>}
            </div>
          )}

          <fieldset className="radio-cards">
            <legend>{t("mkt.payment")}</legend>
            {s.payments.map((p) => (
              <label key={p} className={payment === p ? "radio-card is-selected" : "radio-card"}>
                <input type="radio" name="payment" value={p} checked={payment === p} onChange={() => setPayment(p)} disabled={(p === "cash" && mode !== null && !isInPerson(mode)) || unsafe(p)} />
                <span>
                  <strong>{PAYMENT_LABEL[p][locale]}</strong>
                  <span className="muted small">{t(`mkt.payHint.${p}` as never)}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {cashBlocked && <p className="form-error">{t("mkt.cashInPerson")}</p>}
          {s.limited && <p className="notice notice-warn">🤝 {t("mkt.limitedBuyer")}</p>}
          {safeOnly && <p className="notice notice-warn">{t("mkt.newSellerSafePay", { max: ars(s.safePayAboveArs!) })}</p>}
          {(payment === "transfer" || payment === "crypto") && <p className="notice notice-warn">{t("mkt.manualWarning")}</p>}
          {payment === "cash" && !cashBlocked && <p className="notice">{t("mkt.cashNote")}</p>}

          <fieldset className="stack">
            <legend>{t("mkt.yourDetails")}</legend>
            <div className="field">
              <label htmlFor="mc-name">{t("mkt.name")}</label>
              <input id="mc-name" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} autoComplete="name" required />
            </div>
            <div className="field">
              <label htmlFor="mc-phone">{t("mkt.phone")}</label>
              <input id="mc-phone" type="tel" value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} autoComplete="tel" />
              <span className="field-hint">{t("mkt.phoneHint")}</span>
            </div>
            {mode === "ship" && (
              <>
                <div className="field">
                  <label htmlFor="mc-address">{t("mkt.address")}</label>
                  <input id="mc-address" value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} autoComplete="street-address" required />
                </div>
                <div className="field">
                  <label htmlFor="mc-city">{t("mkt.city")}</label>
                  <input id="mc-city" value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })} autoComplete="address-level2" required />
                </div>
              </>
            )}
            <div className="field">
              <label htmlFor="mc-notes">{t("mkt.notes")}</label>
              <textarea id="mc-notes" rows={2} maxLength={500} value={buyer.notes} onChange={(e) => setBuyer({ ...buyer, notes: e.target.value })} />
            </div>
          </fieldset>
        </div>

        <aside className="checkout-summary stack">
          <dl className="totals">
            <dt>{t("mkt.subtotal")}</dt>
            <dd>{ars(subtotal)}</dd>
            {couponArs > 0 && (
              <>
                <dt>{t("mkt.couponLine", { code: coupon!.code })}</dt>
                <dd>− {ars(couponArs)}</dd>
              </>
            )}
            <dt>{t("mkt.deliveryLine")}</dt>
            <dd>{!mode ? "—" : shipping === null ? t("mkt.toAgree") : shipping === 0 ? t("mkt.free") : ars(shipping)}</dd>
            <dt className="total">{t("mkt.totalTo", { handle })}</dt>
            <dd className="total">{ars(total)}</dd>
          </dl>
          <div className="coupon-box">
            {offerId !== null ? (
              <p className="muted small">{t("mkt.couponWithOffer")}</p>
            ) : coupon ? (
              <p className="small">
                <span className="coupon-chip">{coupon.code}</span>{" "}
                <button type="button" className="btn btn-link btn-sm" onClick={() => setCoupon(null)}>
                  {t("mkt.couponRemove")}
                </button>
              </p>
            ) : (
              <>
                <label htmlFor="mc-coupon" className="small">
                  {t("mkt.couponHave")}
                </label>
                <div className="coupon-input">
                  <input id="mc-coupon" value={couponCode} maxLength={16} onChange={(e) => setCouponCode(e.target.value)} autoCapitalize="characters" />
                  <button type="button" className="btn btn-outline btn-sm" disabled={checking || !couponCode.trim()} onClick={() => void applyCoupon(couponCode)}>
                    {t("mkt.couponApply")}
                  </button>
                </div>
                {(mine.data ?? []).map((c) => (
                  <p key={c.code} className="small">
                    {t("myc.atCheckout", { code: c.code, off: couponOff(c) })}{" "}
                    <button type="button" className="btn btn-link btn-sm" onClick={() => void applyCoupon(c.code)}>
                      {t("mkt.couponUse")}
                    </button>
                  </p>
                ))}
                {s.coupon && !(mine.data ?? []).some((c) => c.code === s.coupon!.code) && (
                  <p className="small">
                    {t("cpn.public", { code: s.coupon.code, off: couponOff(s.coupon) })}{" "}
                    <button type="button" className="btn btn-link btn-sm" onClick={() => void applyCoupon(s.coupon!.code)}>
                      {t("mkt.couponUse")}
                    </button>
                  </p>
                )}
              </>
            )}
            {couponError && <p className="form-error small" role="alert">{couponError}</p>}
          </div>
          <label className="check">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />{" "}
            <span>
              {t("mkt.acceptTermsBuyer")} <Link to="/mercado/terminos">{t("mkt.marketTerms")}</Link>
            </span>
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          {missingForMin > 0 && <p className="form-error small">{t("mkt.belowMinOrder", { min: ars(s.minOrderArs!), missing: ars(missingForMin) })}</p>}
          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={!ready || sending}>
            {payment === "mercadopago" ? t("mkt.payWithMp") : t("mkt.placeOrder")}
          </button>
          <p className="muted small">{t("mkt.noMoneyNote")}</p>
        </aside>
      </form>
    </div>
  );
}
